import { useEffect } from "react";
import "./App.css";
import "./i18n";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import AppLayout from "./components/AppLayout";
import AgeGateModal from "./components/AgeGateModal";
import ErrorBoundary from "./components/ErrorBoundary";
import { BRAND } from "./lib/brand";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Picks from "./pages/Picks";
import PickDetail from "./pages/PickDetail";
import Performance from "./pages/Performance";
import Plans from "./pages/Plans";
import PaymentSuccess from "./pages/PaymentSuccess";
import TrialSuccess from "./pages/TrialSuccess";
import Profile from "./pages/Profile";
import CommunityPage from "./pages/CommunityPage";

import AdminPanel from "./pages/admin/AdminPanel";
import AdminCreatePick from "./pages/admin/AdminCreatePick";
import AdminPicksManage from "./pages/admin/AdminPicksManage";
import AdminUsersPage from "./pages/admin/AdminUsersPage";
import AdminReviewsPage from "./pages/admin/AdminReviewsPage";
import AdminPaymentsPage from "./pages/admin/AdminPaymentsPage";
import AdminConfigPage from "./pages/admin/AdminConfigPage";
import AdminEmailTemplatesPage from "./pages/admin/AdminEmailTemplatesPage";
import AdminBroadcastPage from "./pages/admin/AdminBroadcastPage";
import AdminPaymentMethodsPage from "./pages/admin/AdminPaymentMethodsPage";
import AdminParlaysPage from "./pages/admin/AdminParlaysPage";
import AdminCreateParlay from "./pages/admin/AdminCreateParlay";
import AdminSupportPage from "./pages/admin/AdminSupportPage";
import ParlaysPage from "./pages/ParlaysPage";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ChatPage from "./pages/ChatPage";
import ChatFAB from "./components/ChatFAB";
import HistorialPage from "./pages/HistorialPage";
import OwnerDashboard from "./pages/OwnerDashboard";

function RootRedirect() {
  const { loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-obsidian-900">
        <div className="text-zinc-500 text-sm tracking-widest uppercase animate-pulse">Cargando</div>
      </div>
    );
  }
  return <Landing />;
}

function RootRedirectAuth() {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-obsidian-900">
      <div className="text-zinc-500 text-sm tracking-widest uppercase animate-pulse">Cargando</div>
    </div>
  );
  if (user) return <Navigate to="/jugadas" replace />;
  return <Landing />;
}

function ToasterThemed() {
  const { theme } = useTheme();
  return (
    <Toaster
      theme={theme}
      position="top-right"
      toastOptions={{
        style: theme === "dark"
          ? { background: "#1A1F26", border: "1px solid rgba(255,255,255,0.08)", color: "#fff" }
          : { background: "#F8F9FA", border: "1px solid rgba(0,0,0,0.1)", color: "#1A1F26" },
      }}
    />
  );
}

function AppInner() {
  return (
    <div className="App">
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <AgeGateModal />
            <ToasterThemed />
            <Routes>
            <Route path="/" element={<RootRedirectAuth />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/historial" element={<HistorialPage />} />
            <Route path="/owner" element={<OwnerDashboard />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/trial-success" element={<TrialSuccess />} />

            <Route path="/dashboard" element={
              <ProtectedRoute><AppLayout><Dashboard /></AppLayout></ProtectedRoute>
            } />
            <Route path="/jugadas" element={
              <ProtectedRoute><AppLayout><Picks /></AppLayout></ProtectedRoute>
            } />
            <Route path="/jugadas/:id" element={
              <ProtectedRoute><AppLayout><PickDetail /></AppLayout></ProtectedRoute>
            } />
            {/* Back-compat old URLs */}
            <Route path="/picks" element={<Navigate to="/jugadas" replace />} />
            <Route path="/picks/:id" element={<Navigate to="/jugadas" replace />} />
            <Route path="/performance" element={
              <ProtectedRoute><AppLayout><Performance /></AppLayout></ProtectedRoute>
            } />
            <Route path="/plans" element={
              <ProtectedRoute><AppLayout><Plans /></AppLayout></ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute><AppLayout><Profile /></AppLayout></ProtectedRoute>
            } />
            <Route path="/parlays" element={
              <ProtectedRoute><AppLayout><ParlaysPage /></AppLayout></ProtectedRoute>
            } />
            <Route path="/comunidad" element={
              <ProtectedRoute><AppLayout><CommunityPage /></AppLayout></ProtectedRoute>
            } />

            {/* Admin */}
            <Route path="/admin" element={
              <ProtectedRoute adminOnly><AppLayout><AdminPanel /></AppLayout></ProtectedRoute>
            } />
            <Route path="/admin/analisis" element={
              <ProtectedRoute adminOnly><AppLayout><AdminPicksManage /></AppLayout></ProtectedRoute>
            } />
            <Route path="/admin/analisis/nuevo" element={
              <ProtectedRoute adminOnly><AppLayout><AdminCreatePick /></AppLayout></ProtectedRoute>
            } />
            <Route path="/admin/usuarios" element={
              <ProtectedRoute adminOnly><AppLayout><AdminUsersPage /></AppLayout></ProtectedRoute>
            } />
            <Route path="/admin/reviews" element={
              <ProtectedRoute adminOnly><AppLayout><AdminReviewsPage /></AppLayout></ProtectedRoute>
            } />
            <Route path="/admin/pagos" element={
              <ProtectedRoute adminOnly><AppLayout><AdminPaymentsPage /></AppLayout></ProtectedRoute>
            } />
            <Route path="/admin/config" element={
              <ProtectedRoute adminOnly><AppLayout><AdminConfigPage /></AppLayout></ProtectedRoute>
            } />
            <Route path="/admin/emails" element={
              <ProtectedRoute adminOnly><AppLayout><AdminEmailTemplatesPage /></AppLayout></ProtectedRoute>
            } />
            <Route path="/admin/broadcast" element={
              <ProtectedRoute adminOnly><AppLayout><AdminBroadcastPage /></AppLayout></ProtectedRoute>
            } />
            <Route path="/admin/metodos-pago" element={
              <ProtectedRoute adminOnly><AppLayout><AdminPaymentMethodsPage /></AppLayout></ProtectedRoute>
            } />
            <Route path="/admin/parlays" element={
              <ProtectedRoute adminOnly><AppLayout><AdminParlaysPage /></AppLayout></ProtectedRoute>
            } />
            <Route path="/admin/parlays/nuevo" element={
              <ProtectedRoute adminOnly><AppLayout><AdminCreateParlay /></AppLayout></ProtectedRoute>
            } />
            <Route path="/admin/parlays/editar/:id" element={
              <ProtectedRoute adminOnly><AppLayout><AdminCreateParlay /></AppLayout></ProtectedRoute>
            } />
            <Route path="/admin/soporte" element={
              <ProtectedRoute adminOnly><AppLayout><AdminSupportPage /></AppLayout></ProtectedRoute>
            } />
            <Route path="/admin/picks" element={<Navigate to="/admin/analisis" replace />} />
            <Route path="/admin/picks/new" element={<Navigate to="/admin/analisis/nuevo" replace />} />

            <Route path="*" element={<Navigate to="/" replace />} />

            {/* Chat Asesor BetIQ — público, layout propio sin AppLayout */}
            <Route path="/chat" element={<><ChatPage /><ChatFAB /></>} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </div>
  );
}

export default function App() {
  useEffect(() => { document.title = BRAND.tabTitle; }, []);

  return (
    <ErrorBoundary>
      <AppInner />
    </ErrorBoundary>
  );
}
